import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Loading, Table, Typography } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { getInvoiceDisputeHistoryListAction } from 'store/actions/invoiceActions';
import { getInvoiceDisputeHistoryList } from 'store/sagas/APIs/invoice';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildMaskedDisputeHistoryRow } from 'utils/demoMask';

const DISPUTE_HISTORY_QUERY_PARAMS = {
  search: 'dhSearch',
  page: 'dhPage',
  pageSize: 'dhPageSize',
  sort: 'dhSort',
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().split('T')[0];
};

export const DisputeHistoryTable = ({ data, dropFilterActiveCount, dropFilterActiveSummary }) => {
  const dispatch = useDispatch();
  const { isDemo } = useDemoMask();
  const disputeTableRef = useRef(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);
  const { filter, setFilter, sortOptions, setSortOptions, pagination, setPagination } =
    useListQueryState({
      defaultFilter: {
        options: {},
        searchText: '',
        searchPlaceholder: 'Search Property..',
      },
      defaultRowsPerPage: 12,
      params: DISPUTE_HISTORY_QUERY_PARAMS,
    });
  const normalizedSearchText = `${filter?.searchText || filter?.search || ''}`.trim();
  const disputeHistory = useSelector((state) =>
    state.getIn(['invoice', 'invoiceDisputeHistoryData']),
  );
  const disputeData = disputeHistory?.get('data');
  const disputeTotal = disputeHistory?.get('total');
  const disputeHistoryLoading = Boolean(disputeHistory?.get('isLoading'));
  const disputeHistoryListError = disputeHistory?.get('disputeHistoryListError') || null;

  const pageResetBaselineRef = useRef(null);

  const fetchDisputeHistory = useCallback(() => {
    if (!data?.invoice_id) return;
    dispatch(
      getInvoiceDisputeHistoryListAction({
        invoice_id: data.invoice_id,
        filter: {
          ...filter,
          searchText: normalizedSearchText,
        },
        sortOptions,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
      }),
    );
  }, [
    data?.invoice_id,
    dispatch,
    filter,
    normalizedSearchText,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
  ]);

  useEffect(() => {
    if (!data?.invoice_id) return;
    fetchDisputeHistory();
  }, [data?.invoice_id, fetchDisputeHistory]);

  useEffect(() => {
    const next = {
      search: filter?.searchText ?? '',
      sortKey: JSON.stringify(sortOptions ?? {}),
      invoiceId: data?.invoice_id,
    };
    const prev = pageResetBaselineRef.current;
    pageResetBaselineRef.current = next;
    if (prev == null) return;

    if (
      prev.search === next.search &&
      prev.sortKey === next.sortKey &&
      prev.invoiceId === next.invoiceId
    ) {
      return;
    }

    setPagination((prevPage) =>
      prevPage.pageNumber === 0 ? prevPage : { ...prevPage, pageNumber: 0 },
    );
  }, [filter?.searchText, sortOptions, data?.invoice_id, setPagination]);

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: disputeTotal,
    isLoading: disputeHistoryLoading,
  });

  useEffect(() => {
    const invoiceId = data?.invoice_id;
    if (!invoiceId) {
      setFullExportDatasetTotal(undefined);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getInvoiceDisputeHistoryList({
          invoice_id: invoiceId,
          filter: { searchText: '' },
          sortOptions: {},
          offset: 0,
          limit: 1,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore prefetch failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.invoice_id]);

  const mapDisputeRow = useCallback(
    (dispute) => buildMaskedDisputeHistoryRow(dispute, formatDate, isDemo),
    [isDemo],
  );

  const disputeTotalNum = disputeTotal != null ? Number(disputeTotal) : NaN;

  const handleRequestExportData = useCallback(async () => {
    const invoiceId = data?.invoice_id;
    if (!invoiceId) return [];
    const totalRecords = (Number.isFinite(disputeTotalNum) ? disputeTotalNum : 0) || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInvoiceDisputeHistoryList({
            invoice_id: invoiceId,
            filter: {
              ...filter,
              searchText: normalizedSearchText,
            },
            sortOptions,
            offset,
            limit,
          }),
      });
      return rows.map(mapDisputeRow);
    } catch (e) {
      throw new Error('Unable to export dispute history for the current filter.');
    }
  }, [data?.invoice_id, disputeTotalNum, filter, normalizedSearchText, sortOptions, mapDisputeRow]);

  const handleRequestFullExportData = useCallback(async () => {
    const invoiceId = data?.invoice_id;
    if (!invoiceId) return [];
    const totalRecords =
      Number(fullExportDatasetTotal) ||
      (Number.isFinite(disputeTotalNum) ? disputeTotalNum : 0) ||
      0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInvoiceDisputeHistoryList({
            invoice_id: invoiceId,
            filter: { searchText: '' },
            sortOptions: {},
            offset,
            limit,
          }),
      });
      return rows.map(mapDisputeRow);
    } catch (e) {
      throw new Error('Unable to export the full dispute history dataset.');
    }
  }, [data?.invoice_id, fullExportDatasetTotal, disputeTotalNum, mapDisputeRow]);

  const disputes = useMemo(() => {
    if (disputeData?.toJS) return disputeData.toJS();
    return disputeData || [];
  }, [disputeData]);

  const tableData = useMemo(
    () => disputes.map((dispute) => buildMaskedDisputeHistoryRow(dispute, formatDate, isDemo)),
    [disputes, isDemo],
  );

  return (
    <>
      {disputeHistoryListError && !disputeHistoryLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
          <GlobalUXState variant="apiError" compact description={disputeHistoryListError} />
          <Button variant="primary" size="medium" onClick={fetchDisputeHistory}>
            Retry
          </Button>
        </Box>
      ) : null}
      {disputeHistoryLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <Loading size="large" />
        </Box>
      ) : null}
      <Table
        showEmptyState
        emptyContent={
          <GlobalUXState
            variant={normalizedSearchText ? 'noResults' : 'noData'}
            compact
            description={
              normalizedSearchText
                ? 'No dispute history rows match your search.'
                : 'No dispute history is available for this invoice.'
            }
          />
        }
        ref={disputeTableRef}
        onExportButtonClick={() => setExportModalOpen(true)}
        onRequestExportData={data?.invoice_id ? handleRequestExportData : undefined}
        onRequestFullExportData={data?.invoice_id ? handleRequestFullExportData : undefined}
        title="Dispute History"
        data={tableData}
        filter={filter}
        dropFilter={{
          resetFunc: () => {},
          applyFunc: () => {},
        }}
        dropFilterActiveCount={dropFilterActiveCount}
        dropFilterActiveSummary={dropFilterActiveSummary}
        onChangeFilter={setFilter}
        onChangeRowsPerPage={(value) =>
          setPagination((prev) => ({
            ...prev,
            rowsPerPage: Number(value),
            pageNumber: 0,
          }))
        }
        pagination={{
          ...pagination,
          totalItems: Number(disputeTotal) || 0,
          totalPages: Math.ceil((Number(disputeTotal) || 0) / pagination.rowsPerPage) || 0,
        }}
        onChangePagination={setPagination}
        sortOptions={sortOptions}
        onChangeSort={setSortOptions}
        style={{ width: '100%' }}
        columns={[
          {
            name: '#',
            options: {
              flex: '40px 1 1',
              sort: true,
            },
            key: 'line_id',
          },
          {
            name: 'Service Date',
            options: {
              flex: '120px 1 1',
              sort: true,
            },
            key: 'service_date',
          },
          {
            name: 'Description',
            options: {
              flex: '300px 1 1',
              sort: true,
              customBodyRenderer: (value) => (
                <Typography variant="body2">{value || '-'}</Typography>
              ),
            },
            key: 'description',
          },
          {
            name: 'Amount',
            options: {
              flex: '120px 1 1',
              sort: true,
              customBodyRenderer: (value) => (
                <Typography variant="body2">{value ? `$${value}` : '-'}</Typography>
              ),
            },
            key: 'amount',
          },
          {
            name: 'Reason',
            options: {
              flex: '180px 1 1',
              sort: true,
              customBodyRenderer: (value) =>
                value ? (
                  <Badge color="warmBlue" label={value} rounded textSize="medium" />
                ) : (
                  <Typography variant="body2">-</Typography>
                ),
            },
            key: 'reason',
          },
          {
            name: 'Disputed Date',
            options: {
              flex: '140px 1 1',
              sort: true,
              customBodyRenderer: (value) => (
                <Typography variant="body2">{value || '-'}</Typography>
              ),
            },
            key: 'dispute_date',
          },
        ]}
      />
      <ListExportScopeModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        tableRef={disputeTableRef}
        title="Dispute History"
        currentPageRowCount={tableData.length}
        paginationTotalItems={Number.isFinite(disputeTotalNum) ? disputeTotalNum : undefined}
        fullDatasetTotal={fullExportDatasetTotal}
        enableFiltered={Boolean(data?.invoice_id)}
        enableFull={Boolean(data?.invoice_id)}
        enableSelected={false}
      />
    </>
  );
};
